import { AlertTriangle, Mail } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ResendVerificationButton } from "@/components/email-verification/resend-verification-button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useCurrentContext } from "@/hooks/use-current-context"
import { cn } from "@/lib/utils"

/**
 * UnverifiedEmailBanner — OKS-146, CTA reworked in OKS-647,
 * RELOCATED out of the shells in OKS-799.
 *
 * Renders a non-dismissable prompt whenever `currentUser.role ===
 * "creator"` AND `currentUser.verified === false`. Returns `null` (no
 * DOM) for verified users, non-creators, and unauthenticated visitors —
 * the prompt is creator-scoped per PRD §2.
 *
 * # Where this mounts (OKS-799)
 *
 * NOT in the creator shells. OKS-717 made this the first row under the
 * sticky header on EVERY creator page; the CEO flagged that as an
 * account-hygiene nag taxing 100% of pageviews, and CTPO reversed the
 * placement. It now mounts in exactly two places, both page content:
 *
 *   - `pages/Creator/Profile.tsx` — 2nd child of the content column
 *     (below the dormant band, above the identity hero).
 *   - `layouts/settings/layout.tsx` — once in the shared Settings frame
 *     (below the heading, above the tab nav + tab content), so it shows
 *     on every Settings tab rather than only Settings → Email.
 *
 * The gate itself (OKS-146: unverified creators past grace can't
 * accept/apply) is UNCHANGED and still surfaces contextually at action
 * time via `<CollabGateAlert>`.
 *
 * Consequence worth knowing: `creator_profile_controller.rb` includes
 * `CreatorPaused::RedirectConcern`, so a PAUSED creator is 303'd off
 * `/profile`. The Settings mount is therefore a paused creator's ONLY
 * route to this prompt — a one-link dependency, locked by spec. Don't
 * drop the Settings account-menu entry from the paused shell.
 *
 * # Styling (OKS-799)
 *
 * Self-styles as a CARD, not chrome. It previously hardcoded
 * `rounded-none border-x-0 border-t-0` to fake a full-bleed row bolted
 * under a sticky header; as page content it takes `Alert`'s stock
 * `rounded-lg border` so it matches its neighbours. `className` is
 * merged (not replaced) so callers can own the vertical rhythm their
 * container needs — Profile passes nothing (its column is `gap-5`),
 * Settings passes `mb-8`.
 *
 * Two variants, branched on the server-derived predicate
 * `currentUser.verification_grace_expired`:
 *
 *   - `false` (day 0–29): default Alert variant + Mail icon. Body =
 *     "Verify your email to keep your spot in the crew."
 *   - `true`  (day 30+):  destructive Alert variant + AlertTriangle.
 *     Body =
 *     "Verify your email — you can't accept new collabs until you do."
 *
 * The CTA reuses the shipped `<ResendVerificationButton>` (OKS-647):
 * tapping it POSTs the resend in place via the Inertia router — no
 * navigation to Settings — with the same in-flight/cooldown state
 * machine and Sonner toast the Settings → Email button uses. It keeps
 * its banner-specific `testId`: `<CollabGateAlert>` renders the same
 * component with the DEFAULT testid, so the override is what keeps the
 * two unambiguous when both render (e.g. an unverified creator on
 * `/missions`).
 *
 * Accessibility: `role="status"` (announces once on first render via
 * Alert's default role-alert override; we keep `aria-live="polite"`
 * so subsequent re-renders don't keep yelling). The CTA is now a
 * button-semantics control with an inherited `aria-disabled` state
 * during the in-flight/cooldown window.
 */
interface UnverifiedEmailBannerProps {
  /**
   * Merged onto the root `Alert` — for the consuming container's
   * vertical rhythm only. Do NOT pass border/radius overrides here:
   * the card treatment is the OKS-799 deliverable (UX Spec §2.5).
   */
  className?: string
}

export function UnverifiedEmailBanner({
  className,
}: UnverifiedEmailBannerProps = {}) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()

  if (currentUser?.role !== "creator") return null
  if (currentUser.verified) return null

  const enforced = currentUser.verification_grace_expired
  const variant = enforced ? "destructive" : "default"
  const Icon = enforced ? AlertTriangle : Mail
  const bodyKey = enforced
    ? "email_verification.banner.enforced.body"
    : "email_verification.banner.default.body"

  return (
    <Alert
      variant={variant}
      role="status"
      aria-live="polite"
      data-testid="unverified-email-banner"
      data-variant={variant}
      className={cn(className)}
    >
      <Icon aria-hidden="true" />
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex-1">{t(bodyKey)}</span>
        <ResendVerificationButton
          testId="unverified-email-banner-action"
          className="shrink-0"
        />
      </AlertDescription>
    </Alert>
  )
}
